<script setup lang="ts">
import {computed} from "vue";
import axios from "axios";
import {Button, Icon} from "@/Components/Library";
import {useToast} from "primevue/usetoast";
import Str from "@/Utils/Str";
import {useConfirm} from "primevue/useconfirm";

const toast = useToast();
const confirm = useConfirm();

const props = withDefaults(
    defineProps<{
        file: any;
        modelType: string;
        modelId: string | number;
        collection?: string;
        showDeleteButton?: boolean;
    }>(),
    {
        collection: "files",
        showDeleteButton: true,
    },
);
const emit = defineEmits(["delete"]);
const fileTypeName = computed(() =>
    props.collection
        .split(/(?=[A-Z])/)
        .join(" ")
        .toLowerCase(),
);

const type = computed(
    () =>
        props.file.type ??
        props.file.mime_type?.split("/").pop() ??
        props.file.url?.toString().split(".").pop() ??
        props.file.name?.toString().split(".").pop(),
);

const isImage = computed(() => {
    return type.value.match(/(jpg|jpeg|png|gif|bmp|svg|webp)/);
});

const fileIcon = computed(() => {
    let fileIcon = "bi:file-earmark-pdf";
    switch (type.value) {
        //audio
        case "mp3":
        case "mid":
        case "ogg":
        case "wav":
            fileIcon = "bi:file-earmark-music";
            break;
        //video
        case "webm":
        case "avi":
        case "mpeg":
        case "mp4":
        case "ogv":
        case "3gp":
        case "mkv":
            fileIcon = "bi:file-earmark-play";
            break;
        // images
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
        case "bmp":
        case "svg":
        case "webp":
            fileIcon = "bi:file-earmark-image";
            break;
        //docs
        case "docx":
        case "doc":
        case "xls":
        case "xlsx":
        case "ppt":
        case "pptx":
        case "txt":
        case "csv":
        case "rtf":
        case "odt":
        case "ods":
        case "odp":
            fileIcon = "bi:file-earmark-text";
            break;
        //pdf
        case "pdf":
            fileIcon = "bi:file-earmark-pdf";
            break;
        default:
            fileIcon = "bi:file-earmark";
    }

    return fileIcon;
});

const handleDelete = async () => {
    try {
        console.log("handleDelete");
        const resp = await axios.delete(
            route("media.destroy", {
                modelType: props.modelType,
                modelId: props.modelId,
                collectionName: props.collection,
                mediaId: props.file.id,
            }),
        );
        console.log(resp);

        toast.add({
            severity: "success",
            summary: "Success",
            detail: `${Str.of(fileTypeName.value).capitalise().value} deleted successfully`,
            life: 3000,
        });
        emit("delete", props.file);
    } catch (e) {
        console.log(e);
    }
};

const confirmDelete = (event: Event) => {
    confirm.require({
        target: event.target as HTMLElement,
        header: "Delete file",
        message: `Are you sure you want to delete this ${fileTypeName.value}?`,
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 rounded-lg hover:brightness-110",
        accept: () => {
            handleDelete();
        },
    });
};
</script>

<template>
    <div class="group relative w-fit">
        <Button
            v-if="showDeleteButton"
            type="button"
            :icon="{icon: 'mdi:close'}"
            class="!absolute right-1 top-0 z-10 flex !h-6 !w-6 rounded-full bg-red-600 !p-0 text-white"
            @click.stop="confirmDelete"
        />
        <button type="button" class="relative w-32 cursor-pointer px-3 py-2 text-xs">
            <div class="flex justify-center">
                <img
                    v-if="isImage"
                    :alt="file.name"
                    :src="file.url"
                    class="h-20 w-full rounded-lg object-cover object-center"
                />
                <Icon v-else :icon="fileIcon" :width="'48'" />
            </div>
            <div class="mt-4 break-words text-center">
                {{ file.name }}
            </div>
        </button>
    </div>
</template>

<style scoped></style>
